import { Autocomplete, Box, Checkbox, Chip, Divider, FormControlLabel, Paper, PaperProps, TextField } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useController, UseFormReturn } from "react-hook-form";
import { VirtualizedAutocompleteContext, VirtualizedAutocompleteListbox, VirtualizedAutocompleteNavigation, VirtualizedAutocompleteOption } from "./virtualized_autocomplete_listbox";

type Props<T, F extends {}> = {
  name: keyof F;
  label?: string;
  options: T[];
  required?: boolean;
  chipMaxLength?: number;
  readOnly?: boolean;
  reactHookForm: UseFormReturn<F>;
  getLimitTagsText?: (more: number) => string;
  getOptionDisabled?: (opt: T) => boolean;
  getOptionLabel: (opt: T) => string;
  getOptionKey: (opt: T) => any;
  onChangeValue?: (ids: string[] | number[] | null) => void;
  onClose?: (ids: string[] | number[] | null) => void;
};

const SelectAllContext = createContext({ checked: false, disabled: false, onSelectAll: () => {} });

// A stable slot component preserves the virtual list and its scroll position.
function SelectAllPaper({ children, ...props }: PaperProps) {
  const { checked, disabled, onSelectAll } = useContext(SelectAllContext);
  return (
    <Paper {...props}>
      <Box onMouseDown={event => event.preventDefault()} pl={3} py={0.5}>
        <FormControlLabel
          label="Selecionar Todos"
          disabled={disabled}
          control={<Checkbox checked={checked} onChange={onSelectAll} sx={{ marginRight: 1 }} />}
        />
      </Box>
      <Divider />
      {children}
    </Paper>
  );
}

export const DefaultFormMultiAutocomplete = <T, F extends {},>({
  name,
  label,
  options,
  required,
  chipMaxLength,
  readOnly,
  reactHookForm,
  getLimitTagsText,
  getOptionDisabled,
  getOptionLabel,
  getOptionKey,
  onChangeValue,
  onClose,
}: Props<T, F>) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const navigationRef = useRef<VirtualizedAutocompleteNavigation | null>(null);
  const { field, fieldState, formState } = useController({ name: name as any, control: reactHookForm.control });
  const changeField = field.onChange;
  const fieldValue = field.value as (string | number)[] | undefined;
  const selectedIds = useMemo(() => new Set(fieldValue ?? []), [fieldValue]);
  const selectedOptions = useMemo(
    () => options.filter(option => selectedIds.has(getOptionKey(option))),
    [options, selectedIds, getOptionKey]
  );
  const nativeFilter = useMemo(() => createFilterOptions<T>(), []);
  // Share MUI's accent/case normalization with Select All, without a result limit.
  const filteredOptions = useMemo(
    () => open ? nativeFilter(options, { inputValue, getOptionLabel }) : [],
    [open, nativeFilter, options, inputValue, getOptionLabel]
  );
  const filterOptions = useCallback(() => filteredOptions, [filteredOptions]);
  const selectableOptions = useMemo(
    () => filteredOptions.filter(option => !getOptionDisabled?.(option)),
    [filteredOptions, getOptionDisabled]
  );
  const selectAll = selectableOptions.length > 0
    && selectableOptions.every(option => selectedIds.has(getOptionKey(option)));

  const handleSelectAll = useCallback(() => {
    if (readOnly || formState.disabled || !selectableOptions.length) return;
    const ids = selectAll ? [] : selectableOptions.map(getOptionKey);
    changeField(ids);
    setOpen(false);
    setInputValue("");
    // Preserve the existing bulk-selection callback contract.
    onClose?.(selectAll ? null : ids);
  }, [readOnly, formState.disabled, selectableOptions, selectAll, getOptionKey, changeField, onClose]);

  const selectAllContext = useMemo(() => ({
    checked: selectAll,
    disabled: Boolean(readOnly || formState.disabled || !selectableOptions.length),
    onSelectAll: handleSelectAll,
  }), [selectAll, readOnly, formState.disabled, selectableOptions.length, handleSelectAll]);
  const virtualizationContext = useMemo(() => ({ navigationRef, inputValue }), [inputValue]);
  const helperText = typeof fieldState.error?.message === 'string' ? fieldState.error.message : null;

  return (
    <SelectAllContext.Provider value={selectAllContext}>
      <VirtualizedAutocompleteContext.Provider value={virtualizationContext}>
        <Autocomplete
          size="small"
          multiple
          disableCloseOnSelect
          disableListWrap
          limitTags={1}
          getLimitTagsText={more => getLimitTagsText ? getLimitTagsText(more) : `+${more}`}
          getOptionLabel={getOptionLabel}
          getOptionKey={getOptionKey}
          options={options}
          filterOptions={filterOptions}
          getOptionDisabled={getOptionDisabled}
          disabled={formState.disabled}
          readOnly={readOnly}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => {
            setOpen(false);
            setInputValue("");
            onClose?.(reactHookForm.getValues(name as any) as any);
          }}
          inputValue={inputValue}
          onInputChange={(_, value) => setInputValue(value)}
          value={selectedOptions}
          onBlur={field.onBlur}
          onKeyDown={event => navigationRef.current?.prepareKeyDown(event)}
          onHighlightChange={(_, option, reason) => {
            navigationRef.current?.highlight(option === null ? -1 : filteredOptions.indexOf(option), reason);
          }}
          slots={{ paper: SelectAllPaper }}
          slotProps={{ listbox: { component: VirtualizedAutocompleteListbox } }}
          onChange={(_, valueInput, reason) => {
            if (reason === 'clear') {
              field.onChange([]);
              setInputValue("");
              onChangeValue?.(null);
              onClose?.(null);
            } else if (reason === 'blur') {
              onClose?.(valueInput.map(getOptionKey));
            } else {
              const ids = valueInput.map(getOptionKey);
              field.onChange(ids);
              onChangeValue?.(ids);
              if (reason === 'removeOption') onClose?.(ids);
            }
          }}
          renderValue={(value, getItemProps) => {
            const option = value[0];
            const label = value.length ? getOptionLabel(option) : "";
            const { key, ...itemProps } = value.length ? getItemProps({ index: 0 }) : { key: undefined };
            return (
              <>
                {value.length > 0 && (
                  <Chip
                    {...itemProps}
                    key={key}
                    size="small"
                    label={chipMaxLength && label.length > chipMaxLength ? `${label.substring(0, chipMaxLength)}...` : label}
                  />
                )}
                {value.length > 1 && (getLimitTagsText ? getLimitTagsText(value.length - 1) : ` +${value.length - 1}`)}
              </>
            );
          }}
          renderOption={(props, option, { selected }) => ({
            props,
            label: getOptionLabel(option),
            selected,
          } satisfies VirtualizedAutocompleteOption) as unknown as ReactNode}
          renderInput={params => (
            <TextField
              {...params}
              inputRef={field.ref}
              size="small"
              label={!required ? label : `${label} ${fieldValue?.length ? "*" : ""}`}
              error={helperText !== null}
              helperText={helperText}
              required={required && !fieldValue?.length}
            />
          )}
        />
      </VirtualizedAutocompleteContext.Provider>
    </SelectAllContext.Provider>
  );
};
